import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { Typography } from 'interfaces/typography';
import { 
    updateCurrentDispositions,
    selectCurrentDispositions,
    selectIsSaving
} from 'lib/redux/settings/platformDispositionsSlice';

export function CurrentDispositionsList() {
    const dispatch = useDispatch();
    const platform = useSelector((state: RootState) => state.autodialerSettings.selectedPlatform);
    const currentDispositions = useSelector(selectCurrentDispositions(platform!));
    const isSaving = useSelector(selectIsSaving(platform!));


    const handleRemoveDisposition = (dispositionToRemove: string) => {
        if (platform) {
            const newList = currentDispositions.filter((d: string) => d !== dispositionToRemove);
            dispatch(updateCurrentDispositions({ platform, dispositions: newList }));
        }
    };

    const handleClearAll = () => {
        if (platform) {
            dispatch(updateCurrentDispositions({ platform, dispositions: [] }));
        }
    };

    const handleReorderDisposition = (fromIndex: number, toIndex: number) => {
        if (platform) {
            const newList = [...currentDispositions];
            const [movedItem] = newList.splice(fromIndex, 1);
            newList.splice(toIndex, 0, movedItem);
            dispatch(updateCurrentDispositions({ platform, dispositions: newList }));
        }
    };

    if (currentDispositions.length === 0) {
        return null;
    }

    return (
        <div className="space-y-3">
            <div className="flex justify-between items-center">
                <Typography variant="largeParagraph" fontWeight="500" color="#374151">
                    Current Dispositions ({currentDispositions.length})
                </Typography>
                <button
                    onClick={handleClearAll}
                    className="px-3 py-1 text-sm bg-red-50 text-red-600 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
                    disabled={isSaving}
                >
                    <Typography variant="smallParagraph" color="#DC2626">
                        Clear All
                    </Typography>
                </button>
            </div>
            <div className="space-y-2">
                {currentDispositions.map((disposition: string, index: number) => (
                    <div
                        key={index}
                        className="group flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
                        draggable={!isSaving}
                        onDragStart={(e) => {
                            e.dataTransfer.setData("text/plain", index.toString());
                        }}
                        onDragOver={(e) => {
                            e.preventDefault();
                        }}
                        onDrop={(e) => {
                            e.preventDefault();
                            const fromIndex = parseInt(e.dataTransfer.getData("text/plain"));
                            handleReorderDisposition(fromIndex, index);
                        }}
                    >
                        <div className="flex items-center gap-2">
                            {/* Drag Handle */}
                            <div className="cursor-grab active:cursor-grabbing text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 12h18M3 6h18M3 18h18"/>
                                </svg>
                            </div>
                            <Typography variant="mediumParagraph" color="#374151">
                                {disposition}
                            </Typography>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Move Up/Down Buttons */}
                            <button
                                onClick={() => index > 0 && handleReorderDisposition(index, index - 1)}
                                disabled={index === 0 || isSaving}
                                className="p-1 text-gray-400 hover:text-blue-500 disabled:opacity-30 transition-colors"
                                title="Move up"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="18,15 12,9 6,15"></polyline>
                                </svg>
                            </button>
                            <button
                                onClick={() => index < currentDispositions.length - 1 && handleReorderDisposition(index, index + 1)}
                                disabled={index === currentDispositions.length - 1 || isSaving}
                                className="p-1 text-gray-400 hover:text-blue-500 disabled:opacity-30 transition-colors"
                                title="Move down"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="6,9 12,15 18,9"></polyline>
                                </svg>
                            </button>
                            {/* Remove Button */}
                            <button
                                onClick={() => handleRemoveDisposition(disposition)}
                                className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-all duration-200"
                                title={`Remove "${disposition}"`}
                                disabled={isSaving}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            <div className="text-sm text-gray-500 italic">
                <Typography variant="smallParagraph" color="#6B7280">
                    Drag items or use arrow buttons to reorder dispositions
                </Typography>
            </div>
        </div>
    );
}